"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ImageEditor = void 0;
var _react = require("react");
var _EditMenu = require("./editor/EditMenu");
var _EditPreview = require("./editor/EditPreview");
var _imageEditing = require("./utils/imageEditing");
var _cropUtils = require("./utils/cropUtils");
var _SystemDialog = require("./SystemDialog");
var _jsxRuntime = require("react/jsx-runtime");
const ImageEditor = ({
  image,
  onCancel,
  onSave
}) => {
  const originalEditState = (0, _react.useRef)((0, _imageEditing.mergeEditState)(image.editState));
  const [editState, setEditState] = (0, _react.useState)(() => (0, _imageEditing.mergeEditState)(image.editState));
  const [draftCrop, setDraftCrop] = (0, _react.useState)(() => (0, _imageEditing.mergeEditState)(image.editState).crop);
  const [activeTab, setActiveTab] = (0, _react.useState)('colors');
  const [error, setError] = (0, _react.useState)('');
  const [saving, setSaving] = (0, _react.useState)(false);
  const [sourceAspect, setSourceAspect] = (0, _react.useState)(1);
  const [showCloseDialog, setShowCloseDialog] = (0, _react.useState)(false);
  const previewStyle = (0, _react.useMemo)(() => ({
    filter: (0, _imageEditing.getFilterCss)(editState)
  }), [editState]);
  const cropHasChanges = !areCropsEqual(editState.crop, draftCrop, sourceAspect);
  const hasUnsavedChanges = cropHasChanges || !areEditStatesEqual(originalEditState.current, editState, sourceAspect);
  const updateAdjustment = (field, value) => {
    setEditState(current => ({
      ...current,
      adjustments: {
        ...current.adjustments,
        [field]: Number(value)
      }
    }));
  };
  const updateCrop = (field, value) => {
    setDraftCrop(current => (0, _cropUtils.clampCropToAspect)(typeof field === 'object' ? {
      ...current,
      ...field
    } : {
      ...current,
      [field]: field === 'aspect' || field === 'orientation' ? value : Number(value)
    }, sourceAspect));
  };
  const updateFilter = filter => {
    setEditState(current => ({
      ...current,
      filter
    }));
  };
  const updateMode = mode => {
    setEditState(current => ({
      ...current,
      mode
    }));
  };
  const confirmCrop = () => {
    setEditState(current => ({
      ...current,
      crop: (0, _cropUtils.clampCropToAspect)(draftCrop, sourceAspect)
    }));
  };
  const discardCrop = () => {
    setDraftCrop(editState.crop);
  };
  const resetEdits = () => {
    const nextState = (0, _imageEditing.mergeEditState)();
    setEditState(nextState);
    setDraftCrop(nextState.crop);
  };
  const changeTab = tab => {
    setActiveTab(tab);
  };
  const saveEdits = async stateToSave => {
    try {
      setError('');
      setSaving(true);
      const exported = await (0, _imageEditing.exportEditedImage)(image, stateToSave);
      await onSave(image.id, stateToSave, exported);
    } catch (saveError) {
      setSaving(false);
      setError(saveError instanceof Error ? saveError.message : 'The edited image could not be saved.');
    }
  };
  const handleSave = () => saveEdits(editState);
  const handleClose = () => {
    if (saving) {
      return;
    }
    if (!hasUnsavedChanges) {
      onCancel();
      return;
    }
    setShowCloseDialog(true);
  };
  const handleSaveAndClose = () => {
    setShowCloseDialog(false);
    const stateToSave = cropHasChanges ? {
      ...editState,
      crop: (0, _cropUtils.clampCropToAspect)(draftCrop, sourceAspect)
    } : editState;
    saveEdits(stateToSave);
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    className: "editor-backdrop",
    role: "presentation",
    children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("section", {
      className: "editor",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": "editor-title",
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_EditMenu.EditMenu, {
        activeTab: activeTab,
        cropHasChanges: cropHasChanges,
        editState: editState,
        image: image,
        onAdjustmentChange: updateAdjustment,
        onCropChange: updateCrop,
        onFilterChange: updateFilter,
        onModeChange: updateMode,
        onReset: resetEdits,
        onSave: handleSave,
        onTabChange: changeTab,
        saving: saving,
        stagedCrop: draftCrop
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_EditPreview.EditPreview, {
        crop: activeTab === 'crop' ? draftCrop : editState.crop,
        cropHasChanges: cropHasChanges,
        error: error,
        image: image,
        isCropping: activeTab === 'crop',
        onClose: handleClose,
        onConfirmCrop: confirmCrop,
        onCropChange: updateCrop,
        onDiscardCrop: discardCrop,
        previewStyle: previewStyle,
        sourceAspect: sourceAspect,
        onSourceAspectChange: setSourceAspect
      })]
    }), showCloseDialog && /*#__PURE__*/(0, _jsxRuntime.jsx)(_SystemDialog.SystemDialog, {
      title: "Unsaved changes",
      cancelLabel: "Discard changes",
      confirmLabel: "Save changes",
      confirming: saving,
      onCancel: onCancel,
      onClose: () => setShowCloseDialog(false),
      onConfirm: handleSaveAndClose,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        className: "mb-0",
        children: "Would you like to save your changes before closing the editor?"
      })
    })]
  });
};
exports.ImageEditor = ImageEditor;
function areCropsEqual(first, second, sourceAspect) {
  const a = (0, _cropUtils.clampCropToAspect)(first, sourceAspect);
  const b = (0, _cropUtils.clampCropToAspect)(second, sourceAspect);
  return a.aspect === b.aspect && a.orientation === b.orientation && a.size === b.size && a.x === b.x && a.y === b.y;
}
function areEditStatesEqual(first, second, sourceAspect) {
  const a = (0, _imageEditing.mergeEditState)(first);
  const b = (0, _imageEditing.mergeEditState)(second);
  return a.mode === b.mode && a.filter === b.filter && a.adjustments.saturation === b.adjustments.saturation && a.adjustments.hue === b.adjustments.hue && a.adjustments.brightness === b.adjustments.brightness && areCropsEqual(a.crop, b.crop, sourceAspect);
}